import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { PortalTopNav } from '@/components/portal/PortalTopNav';
import { DemoModeBanner } from '@/components/portal/DemoModeBanner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Helmet } from '@/components/Helmet';
import { demoDataProvider } from '@/demo/demoProvider';
import type { DemoDomain } from '@/demo/demoFixtures';
import { isDemoUserAllowed } from '@/demo/demoAccess';
import { logDemoAuditEvent } from '@/demo/demoAudit';
import { ShieldCheck, ShieldAlert, ArrowRight } from 'lucide-react';
import { STATUS_LABELS } from '@/lib/vocabulary/securityTerminology';

const statusLabel = (status: DemoDomain['health_status']) => {
  switch (status) {
    case 'healthy':
      return { label: STATUS_LABELS.covered, tone: 'success' as const, icon: ShieldCheck };
    case 'high_risk':
      return { label: STATUS_LABELS.actionRequired, tone: 'danger' as const, icon: ShieldAlert };
    case 'attention':
      return { label: STATUS_LABELS.needsHost, tone: 'warning' as const, icon: ShieldAlert };
    default:
      return { label: STATUS_LABELS.monitoringPlanned, tone: 'neutral' as const, icon: ShieldAlert };
  }
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Unknown';
  return new Date(value).toLocaleString();
};

export default function DemoDashboard() {
  const { user, loading } = useAuth();
  const { checking } = useRequireMfa();
  const navigate = useNavigate();
  const [domains, setDomains] = useState<DemoDomain[]>([]);

  const canAccess = useMemo(() => isDemoUserAllowed(user), [user]);

  useEffect(() => {
    if (!checking && !loading && user && !canAccess) {
      navigate('/portal', { replace: true });
    }
  }, [checking, loading, user, canAccess, navigate]);

  useEffect(() => {
    let mounted = true;
    demoDataProvider.listDomains().then((data) => {
      if (mounted) setDomains(data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!user || !canAccess) return;
    logDemoAuditEvent('demo_mode_entered', { page: 'demo_workspace' }, 'Demo Mode entered');
  }, [canAccess, user]);

  if (checking || loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary">Loading...</div>
      </div>
    );
  }

  if (!canAccess) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>Demo Workspace | ShadowScan Portal</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <PortalTopNav
        userEmail={user.email || ''}
        userName={user.user_metadata?.full_name || undefined}
        active="demo"
      />

      <main className="relative z-10 container mx-auto px-4 py-8 max-w-6xl space-y-6">
        <DemoModeBanner />

        <Card className="border-border/60">
          <CardHeader>
            <CardTitle>Demo Workspace</CardTitle>
            <CardDescription>
              Explore simulated Security Health, protection coverage, Security Activity, and evidence exports without touching live customer data.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="rounded-md border border-border/60 bg-muted/20 p-4 text-sm space-y-2">
              <div className="font-semibold">Guided Demo</div>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>See critical coverage issues on the Ecommerce Store.</li>
                <li>Review Security Activity evidence for the Bakery site.</li>
                <li>Export demo evidence for a simulated audit snapshot.</li>
                <li>Switch policy tiers to see simulated enforcement posture.</li>
              </ol>
            </div>
            <div className="rounded-md border border-border/60 bg-muted/10 p-4 text-sm space-y-2">
              <div className="font-semibold">Demo Mode</div>
              <p className="text-muted-foreground">Demo data is fully isolated and read-only. No changes affect real customer sites.</p>
              <Button variant="outline" size="sm" onClick={() => navigate('/portal')}>
                Exit demo workspace
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-3">
          {domains.map((domain) => {
            const status = statusLabel(domain.health_status);
            const StatusIcon = status.icon;
            return (
              <Card key={domain.site_id} className="border-border/60">
                <CardHeader className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">{domain.label}</div>
                    <Badge variant="outline">DEMO</Badge>
                  </div>
                  <CardDescription className="text-xs text-muted-foreground">{domain.domain}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <StatusIcon className="h-4 w-4" />
                    <span>Security Health: {status.label}</span>
                    <Badge variant={status.tone}>{status.label}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">Last check-in: {formatTimestamp(domain.last_seen_at)}</div>
                  <div className="text-xs text-muted-foreground">Issues requiring action: {domain.issues_requiring_action}</div>
                  <Button
                    size="sm"
                    className="w-full justify-between"
                    onClick={() => navigate(`/portal/demo/domains/${domain.site_id}`)}
                  >
                    View demo domain
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}
